// mindmetal-app/web/src/pages/employee/mindmetal/ConfigTab.tsx
import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useQuery } from '@tanstack/react-query'
import {
  Settings,
  Cpu,
  Sliders,
  ToggleLeft,
  Save,
  Layers,
  Zap,
  Syringe,
  BarChart3,
  Brain,
  Play,
} from 'lucide-react'
import { Card, CardHeader, CardTitle, CardContent, Button, Input, Select, Badge, api } from '@personic/shared'
import {
  useMindMetaLConfig,
  useMindMetaLProviders,
  useUpdateMindMetaLConfig,
} from '@/hooks/useMindMetaLConfig'
import type { MindMetaLConfig } from '@personic/shared'

export function ConfigTab() {
  const { data: config, isLoading } = useMindMetaLConfig()
  const { data: providers } = useMindMetaLProviders()
  const updateConfig = useUpdateMindMetaLConfig()

  const [formData, setFormData] = useState<Partial<MindMetaLConfig>>({})
  const [hasChanges, setHasChanges] = useState(false)

  const [distillCanonicalId, setDistillCanonicalId] = useState('')
  const [distilling, setDistilling] = useState(false)
  const [distillResult, setDistillResult] = useState<string | null>(null)

  useEffect(() => {
    if (config) {
      setFormData(config)
    }
  }, [config])

  const handleChange = <K extends keyof MindMetaLConfig>(key: K, value: MindMetaLConfig[K]) => {
    setFormData((prev) => ({ ...prev, [key]: value }))
    setHasChanges(true)
  }

  const handleSave = async () => {
    await updateConfig.mutateAsync(formData)
    setHasChanges(false)
  }

  const handleManualDistill = async () => {
    if (!distillCanonicalId.trim()) return
    setDistilling(true)
    setDistillResult(null)
    try {
      const res = await api.post(`/api/mindmetal/v3/admin/test-users/${distillCanonicalId}/distill`, {
        content: 'Manual trigger from admin config',
        content_type: 'monolog',
        tiers: ['universal', 'behavioral'],
      })
      setDistillResult(`Success: ${res.data.signals_extracted} signals extracted`)
    } catch (err: any) {
      setDistillResult(`Error: ${err.response?.data?.detail || err.message}`)
    } finally {
      setDistilling(false)
    }
  }

  // Metrics query
  const { data: metrics } = useQuery({
    queryKey: ['mindmetal-metrics'],
    queryFn: async () => {
      const res = await api.get('/admin/mindmetal/metrics')
      return res.data
    },
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-copper-600" />
      </div>
    )
  }

  const selectedProvider = providers?.find((p) => p.id === formData.distillationProvider)

  const providerOptions = [
    { value: '', label: 'Select provider...' },
    ...(providers?.map((p) => ({ value: p.id, label: p.name })) || []),
  ]

  const modelOptions = [
    { value: '', label: 'Select model...' },
    ...(selectedProvider?.models.map((m) => ({
      value: m.id,
      label: m.recommended ? `${m.name} (Recommended)` : m.name,
    })) || []),
  ]

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="grid lg:grid-cols-4 gap-6"
    >
      {/* Main config column */}
      <div className="lg:col-span-3 space-y-6">
        {/* LLM Provider Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Cpu className="w-5 h-5 text-copper-600" />
              LLM Provider
            </CardTitle>
          </CardHeader>
          <CardContent className="grid md:grid-cols-2 gap-4">
            <Select
              label="Provider"
              value={formData.distillationProvider || ''}
              onChange={(e) =>
                handleChange('distillationProvider', e.target.value as MindMetaLConfig['distillationProvider'])
              }
              options={providerOptions}
            />

            <Select
              label="Model"
              value={formData.distillationModel || ''}
              onChange={(e) => handleChange('distillationModel', e.target.value)}
              disabled={!selectedProvider}
              options={modelOptions}
            />
          </CardContent>
        </Card>

        {/* Distillation Mode */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-copper-600" />
              Distillation Mode
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { value: 'unified', label: 'Unified', desc: 'Personality + Preferences' },
                { value: 'preference_only', label: 'Preferences Only', desc: 'Privacy-focused' },
                { value: 'personality_only', label: 'Personality Only', desc: 'Research mode' },
                { value: 'domain_targeted', label: 'Domain Targeted', desc: 'Specific domains' },
              ].map((mode) => (
                <button
                  key={mode.value}
                  type="button"
                  onClick={() =>
                    handleChange('distillationMode', mode.value as MindMetaLConfig['distillationMode'])
                  }
                  className={`p-4 rounded-xl border text-left transition-all ${
                    formData.distillationMode === mode.value
                      ? 'border-copper-500 bg-copper-50'
                      : 'border-warm-200 hover:border-warm-300'
                  }`}
                >
                  <div className="font-medium text-midnight-800">{mode.label}</div>
                  <div className="text-xs text-warm-500 mt-1">{mode.desc}</div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Extraction Components */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Layers className="w-5 h-5 text-copper-600" />
              Extraction Components
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-warm-500 mb-4">
              Toggle individual extraction components for the distillation pipeline.
            </p>
            <div className="grid md:grid-cols-2 gap-3">
              {[
                { key: 'personalityDistillationEnabled', label: 'Personality Extraction', desc: 'Big Five traits, communication style, cognitive style' },
                { key: 'preferencesDistillationEnabled', label: 'Preference Extraction', desc: 'Explicit and inferred user preferences' },
                { key: 'domainPreferencesEnabled', label: 'Domain Preferences', desc: 'Music, dining, travel, entertainment preferences' },
                { key: 'personaGenerationEnabled', label: 'Relationship Extraction', desc: 'Cross-entity relationship mapping' },
              ].map((comp) => (
                <div
                  key={comp.key}
                  className="flex items-start justify-between p-4 bg-warm-50 rounded-xl hover:bg-warm-100 transition-colors"
                >
                  <div className="pr-4">
                    <div className="font-medium text-midnight-700 text-sm">{comp.label}</div>
                    <div className="text-xs text-warm-500 mt-0.5">{comp.desc}</div>
                  </div>
                  <div
                    className={`w-11 h-6 rounded-full p-1 transition-colors cursor-pointer flex-shrink-0 ${
                      formData[comp.key as keyof MindMetaLConfig]
                        ? 'bg-copper-500'
                        : 'bg-warm-300'
                    }`}
                    onClick={() =>
                      handleChange(
                        comp.key as keyof MindMetaLConfig,
                        !formData[comp.key as keyof MindMetaLConfig] as never
                      )
                    }
                  >
                    <div
                      className={`w-4 h-4 rounded-full bg-white transition-transform ${
                        formData[comp.key as keyof MindMetaLConfig] ? 'translate-x-5' : ''
                      }`}
                    />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Trigger Mode */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-copper-600" />
              Trigger Mode
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3">
              {[
                { value: 'auto', label: 'Automatic', desc: 'Distillation runs automatically when thresholds are met' },
                { value: 'manual', label: 'Manual', desc: 'Distillation only runs when explicitly triggered' },
              ].map((mode) => (
                <button
                  key={mode.value}
                  type="button"
                  onClick={() => handleChange('distillationMode', mode.value as MindMetaLConfig['distillationMode'])}
                  className={`p-4 rounded-xl border text-left transition-all ${
                    (formData.distillationMode === mode.value ||
                      (mode.value === 'manual' && ['unified', 'preference_only', 'personality_only', 'domain_targeted'].includes(formData.distillationMode || '')))
                      ? 'border-copper-500 bg-copper-50'
                      : 'border-warm-200 hover:border-warm-300'
                  }`}
                >
                  <div className="font-medium text-midnight-800">{mode.label}</div>
                  <div className="text-xs text-warm-500 mt-1">{mode.desc}</div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Profile Injection Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Syringe className="w-5 h-5 text-copper-600" />
              Profile Injection
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-warm-50 rounded-lg">
                <div>
                  <span className="text-midnight-700 font-medium text-sm">Enable Profile Injection</span>
                  <p className="text-xs text-warm-500 mt-0.5">
                    Inject personality context into LLM system prompts
                  </p>
                </div>
                <div
                  className={`w-11 h-6 rounded-full p-1 transition-colors cursor-pointer ${
                    formData.injectionEnabled ? 'bg-copper-500' : 'bg-warm-300'
                  }`}
                  onClick={() => handleChange('injectionEnabled', !formData.injectionEnabled)}
                >
                  <div
                    className={`w-4 h-4 rounded-full bg-white transition-transform ${
                      formData.injectionEnabled ? 'translate-x-5' : ''
                    }`}
                  />
                </div>
              </div>
              <Input
                type="number"
                label="Max Profile Size (tokens)"
                value={formData.maxProfileTokens || 4096}
                onChange={(e) => handleChange('maxProfileTokens', parseInt(e.target.value))}
                hint="Maximum token count for injected persona document"
              />
            </div>
          </CardContent>
        </Card>

        {/* Extraction Thresholds */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sliders className="w-5 h-5 text-copper-600" />
              Extraction Thresholds
            </CardTitle>
          </CardHeader>
          <CardContent className="grid md:grid-cols-3 gap-4">
            <Input
              type="number"
              label="Token Threshold"
              value={formData.tokenThreshold || 2000}
              onChange={(e) => handleChange('tokenThreshold', parseInt(e.target.value))}
              hint="Minimum tokens before extraction"
            />
            <Input
              type="number"
              label="Confidence Minimum"
              value={formData.confidenceMinimum || 0.3}
              onChange={(e) => handleChange('confidenceMinimum', parseFloat(e.target.value))}
              step={0.1}
              min={0}
              max={1}
              hint="0.0 - 1.0"
            />
            <Input
              type="number"
              label="Min Exchange Count"
              value={formData.minExchangeCount || 4}
              onChange={(e) => handleChange('minExchangeCount', parseInt(e.target.value))}
              hint="Minimum conversation turns"
            />
          </CardContent>
        </Card>

        {/* Feature Flags */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ToggleLeft className="w-5 h-5 text-copper-600" />
              Feature Flags
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-4">
              {[
                { key: 'personalityDistillationEnabled', label: 'Personality Distillation' },
                { key: 'preferencesDistillationEnabled', label: 'Preference Distillation' },
                { key: 'personaGenerationEnabled', label: 'Persona Generation' },
                { key: 'domainPreferencesEnabled', label: 'Domain Preferences' },
                { key: 'calibrationEnabled', label: 'Calibration System' },
                { key: 'auditLoggingEnabled', label: 'Audit Logging' },
              ].map((flag) => (
                <label
                  key={flag.key}
                  className="flex items-center justify-between p-3 bg-warm-50 rounded-lg cursor-pointer hover:bg-warm-100 transition-colors"
                >
                  <span className="text-midnight-700">{flag.label}</span>
                  <div
                    className={`w-12 h-6 rounded-full p-1 transition-colors cursor-pointer ${
                      formData[flag.key as keyof MindMetaLConfig]
                        ? 'bg-copper-500'
                        : 'bg-warm-300'
                    }`}
                    onClick={() =>
                      handleChange(
                        flag.key as keyof MindMetaLConfig,
                        !formData[flag.key as keyof MindMetaLConfig] as never
                      )
                    }
                  >
                    <div
                      className={`w-4 h-4 rounded-full bg-white transition-transform ${
                        formData[flag.key as keyof MindMetaLConfig] ? 'translate-x-6' : ''
                      }`}
                    />
                  </div>
                </label>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Manual Distillation */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Play className="w-5 h-5 text-copper-600" />
              Quick Distillation
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-warm-500 mb-4">
              Trigger a distillation for a specific test user by canonical ID.
            </p>
            <div className="flex gap-3">
              <Input
                label="Canonical ID"
                value={distillCanonicalId}
                onChange={(e) => setDistillCanonicalId(e.target.value)}
                placeholder="e.g. TUser001"
                className="flex-1"
              />
              <div className="flex items-end">
                <Button
                  onClick={handleManualDistill}
                  isLoading={distilling}
                  disabled={!distillCanonicalId.trim()}
                  leftIcon={<Play className="w-4 h-4" />}
                >
                  Run
                </Button>
              </div>
            </div>
            {distillResult && (
              <div
                className={`mt-3 p-3 rounded-lg text-sm ${
                  distillResult.startsWith('Error')
                    ? 'bg-red-50 text-red-700'
                    : 'bg-sage-50 text-sage-700'
                }`}
              >
                {distillResult}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button
            onClick={handleSave}
            isLoading={updateConfig.isPending}
            disabled={!hasChanges}
            leftIcon={<Save className="w-4 h-4" />}
          >
            Save Configuration
          </Button>
        </div>
      </div>

      {/* Metrics Sidebar */}
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <BarChart3 className="w-5 h-5 text-copper-600" />
              System Metrics
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <MetricRow label="Total Profiles" value={metrics?.total_profiles ?? '—'} />
            <MetricRow label="Active Profiles" value={metrics?.active_profiles ?? '—'} />
            <MetricRow label="Avg Observations" value={metrics?.avg_observations ?? '—'} />
            <MetricRow
              label="Distillation Rate"
              value={metrics?.distillation_rate != null ? `${(metrics.distillation_rate * 100).toFixed(0)}%` : '—'}
            />

            {/* Coverage distribution */}
            {metrics?.coverage_distribution && (
              <div className="pt-3 border-t border-warm-200">
                <div className="text-xs text-warm-500 font-medium mb-2">Coverage Distribution</div>
                <div className="space-y-2">
                  {(['minimal', 'emerging', 'established', 'mature'] as const).map((level) => {
                    const count = metrics.coverage_distribution[level] || 0
                    const total = metrics.active_profiles || 1
                    const pct = Math.round((count / total) * 100)
                    return (
                      <div key={level} className="space-y-1">
                        <div className="flex justify-between text-xs">
                          <span className="text-midnight-700 capitalize">{level}</span>
                          <span className="text-warm-500">{count}</span>
                        </div>
                        <div className="h-1.5 bg-warm-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-copper-400 to-copper-500 rounded-full transition-all"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Status */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Brain className="w-5 h-5 text-copper-600" />
              Pipeline Status
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-midnight-700">Distillation</span>
              <Badge variant={formData.enabled !== false ? 'sage' : 'default'} size="sm">
                {formData.enabled !== false ? 'Active' : 'Disabled'}
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-midnight-700">Injection</span>
              <Badge variant={formData.injectionEnabled ? 'sage' : 'default'} size="sm">
                {formData.injectionEnabled ? 'Active' : 'Disabled'}
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-midnight-700">Persona Gen</span>
              <Badge variant={formData.personaGenerationEnabled ? 'sage' : 'default'} size="sm">
                {formData.personaGenerationEnabled ? 'Active' : 'Disabled'}
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-midnight-700">Calibration</span>
              <Badge variant={formData.calibrationEnabled ? 'sage' : 'default'} size="sm">
                {formData.calibrationEnabled ? 'Active' : 'Disabled'}
              </Badge>
            </div>
          </CardContent>
        </Card>
      </div>
    </motion.div>
  )
}

function MetricRow({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-warm-500">{label}</span>
      <span className="text-sm font-semibold text-midnight-800">{value}</span>
    </div>
  )
}
